<template>
  <div
    v-if="FF_DISPLAY_PERSONALIZED_FEED_DEBUG_INFO"
    class="mb-12 flex flex-wrap gap-8"
  >
    <div>Content cards: {{ contentCards.length }}</div>
    <div>isRequestingContentCards (Braze): {{ isRequestingContentCards }}</div>
    <div>isLoading: {{ isLoading }}</div>
  </div>

  <BlockTitle :title />

  <div
    ref="tabs-scroller"
    class="mb-20 mt-4 flex grow flex-col justify-between overflow-scroll scrollbar-none"
    :class="maskClass"
  >
    <div
      :aria-label="title || ''"
      class="flex w-max items-start gap-8 overflow-x-auto overflow-y-hidden scrollbar-none"
      role="tablist"
    >
      <template v-if="isRequestingContentCards || isLoading">
        <RevSkeleton height="40px" shape="rectangle" width="130px" />
        <RevSkeleton height="40px" shape="rectangle" width="130px" />
      </template>

      <RevChip
        v-for="(tab, tabIndex) in contentCards"
        v-else
        :id="`tab-${tabIndex}`"
        :key="tab.id"
        ref="tabItem"
        aria-controls="content-card-panel"
        :aria-selected="activeTabIndex === tabIndex"
        class="shrink-0"
        :label="tab.title"
        :model-value="activeTabIndex === tabIndex"
        role="tab"
        :tabindex="activeTabIndex === tabIndex ? 0 : -1"
        @click="() => onTabClick(tabIndex)"
        @keydown.left.prevent="() => onTabClick(tabIndex - 1)"
        @keydown.right.prevent="() => onTabClick(tabIndex + 1)"
      />
    </div>
  </div>

  <div
    id="content-card-panel"
    :aria-labelledby="`tab-${activeTabIndex}`"
    role="tabpanel"
  >
    <RevSkeleton
      v-if="isRequestingContentCards || isLoading"
      height="362px"
      shape="rectangle"
      width="100%"
    />
    <component
      :is="currentContentCard?.tag || null"
      v-show="!isLoading"
      v-bind="currentContentCard?.props"
      :data-test="currentContentCard?.id"
      @error="handleCardError"
      @loaded="handleCardLoaded"
    />
    <div v-if="hasError">Something went wrong</div>
  </div>
</template>

<script lang="ts" setup>
import { useRuntimeConfig } from '#imports'
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'

import type { PersonalizedFeedApiProps } from '@backmarket/http-api/src/api-specs-content/models/personalized-feed'
import { getBrazeInstance } from '@backmarket/nuxt-module-tracking/getBrazeInstance'
import { RevChip } from '@ds/components/Chip'
import { RevSkeleton } from '@ds/components/Skeleton'

import { useScrollMaskClass } from '../../composables/useScrollMaskClass'
import type { BlockProps } from '../../models/content-block'
import BlockTitle from '../../shared-components/BlockTitle/BlockTitle.vue'

import {
  type ContentCard,
  transformContentCards,
} from './PersonalizedFeed.utils'

export type PersonalizedFeedBlockProps = PersonalizedFeedApiProps & BlockProps

const { feed } = defineProps<PersonalizedFeedBlockProps>()
const { FF_DISPLAY_PERSONALIZED_FEED_DEBUG_INFO = false } =
  useRuntimeConfig().public

const braze = getBrazeInstance()
let subscriptionId: string | undefined

const isRequestingContentCards = ref(true)
const isLoading = ref(true)
const hasError = ref(false)

const activeTabIndex = ref(0)
const tabItem = useTemplateRef('tabItem')
const scrollerRef = useTemplateRef<HTMLDivElement>('tabs-scroller')
const { maskClass, onScrollAreaChange } = useScrollMaskClass(scrollerRef)

const contentCards = ref<ContentCard[]>([])
const currentContentCard = computed(
  () => contentCards.value[activeTabIndex.value],
)
onMounted(() => {
  braze?.requestContentCardsRefresh()

  subscriptionId = braze?.subscribeToContentCardsUpdates(({ cards }) => {
    contentCards.value = transformContentCards({ cards, feed })

    isRequestingContentCards.value = false

    if (contentCards.value.length === 0) {
      isLoading.value = false
    }
  })
})

onBeforeUnmount(() => {
  if (subscriptionId) {
    braze?.removeSubscription(subscriptionId)
  }
})

function onTabClick(i: number) {
  hasError.value = false

  let index = i
  if (index < 0) index = 0
  if (index >= contentCards.value.length) index = contentCards.value.length - 1

  activeTabIndex.value = index

  requestAnimationFrame(() => {
    const targetTab = tabItem.value?.find(
      (el) => el?.$attrs.id === `tab-${i}`,
    )?.$el
    if (targetTab) {
      targetTab.focus()
      targetTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }
  })
}

function handleCardLoaded() {
  isLoading.value = false
  isRequestingContentCards.value = false
  onScrollAreaChange()
}

function handleCardError() {
  handleCardLoaded()

  hasError.value = true
}
</script>
